
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  PaginationVM,
  RafScoreExecutionDay,
  RafScoreExecutionRun
} from '@/models'
import {
  getRafScoreDailyHistory,
  getRafScoreRunsByDate
} from '@/components/admin/raf-score-events.service'
import moment from 'moment'

type DayRow = RafScoreExecutionDay & {
  _runs?: RafScoreExecutionRun[]
  _loadingRuns?: boolean
  _showDetails?: boolean
}

@Component({
  name: 'RafScoreHistory'
})
export default class RafScoreHistory extends Vue {
  public fields = [
    { key: 'expand', label: '', sortable: false },
    { key: 'index', label: '#', sortable: false },
    { key: 'executionDate', label: 'Date', sortable: false },
    { key: 'hadExecution', label: 'Executed', sortable: false },
    { key: 'runCount', label: 'Runs', sortable: false },
    { key: 'lastStatus', label: 'Last Status', sortable: false },
    { key: 'lastEventDate', label: 'Last Event Time', sortable: false }
  ]
  public stepFields = [
    { key: 'eventDate', label: 'Time', sortable: false },
    { key: 'status', label: 'Status', sortable: false }
  ]
  public items: DayRow[] = []
  public perPage = 20
  public pageOptions = [10, 20, 50, 100]
  public pagination = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  } as PaginationVM

  @Watch('perPage')
  perPageChange() {
    this.load(1)
  }

  get _pagination() {
    return this.pagination
  }

  public async created() {
    await this.load(1)
  }

  public async onPageChange(pageNumber: number) {
    await this.load(pageNumber)
  }

  public async load(pageNumber = 1) {
    const response = await getRafScoreDailyHistory(pageNumber, this.perPage)
    this.items = (response.data || []).map(d => ({
      ...d,
      _runs: undefined,
      _loadingRuns: false
    }))
    const header = response.headers['x-pagination']
    if (header) {
      this.pagination = JSON.parse(header)
    }
  }

  public async toggleDay(row: {
    item: DayRow
    detailsShowing: boolean
    toggleDetails: () => void
  }) {
    const opening = !row.detailsShowing
    row.toggleDetails()
    if (!opening) {
      return
    }
    if (row.item._runs || row.item._loadingRuns) {
      return
    }
    this.$set(row.item, '_loadingRuns', true)
    try {
      const date = moment(row.item.executionDate).format('YYYY-MM-DD')
      const response = await getRafScoreRunsByDate(date)
      this.$set(row.item, '_runs', response.data || [])
    } finally {
      this.$set(row.item, '_loadingRuns', false)
    }
  }

  public rowClass() {
    return 'cursor-default'
  }

  public outcomeVariant(outcome: string) {
    switch ((outcome || '').toLowerCase()) {
      case 'success':
        return 'success'
      case 'failed':
        return 'danger'
      case 'incomplete':
        return 'warning'
      default:
        return 'secondary'
    }
  }

  public shortId(eventId?: string) {
    if (!eventId) return '—'
    return eventId.substring(0, 8)
  }

  public isSuccessStatus(status?: string) {
    return (status || '').toLowerCase().includes('successfully')
  }

  public isFailedStatus(status?: string) {
    const s = (status || '').toLowerCase()
    return s.includes('error') || s.includes('fail') || s.includes('exception')
  }

  public formatDate(value: string | Date) {
    if (!value) return ''
    return moment(value).format('MM/DD/YYYY')
  }

  public formatDateTime(value: string | Date) {
    if (!value) return ''
    return moment(value).format('MM/DD/YYYY hh:mm:ss A')
  }
}
